import {
  type QueryFunction,
  type QueryKey,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import {
  addDoc,
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { useCallback } from 'react'
import * as z from 'zod'

import { getCurrentUser } from './authStore'
import { DocumentNotFoundError } from './firestoreErrors'
import {
  createDocSchema,
  type DocID,
  type TDoc,
  type TStrictResult,
} from './firestoreSchemas'
import { getDbInstance } from './runtime'
import { withWriteTimeout } from './withWriteTimeout'

const getUserUID = (): string => {
  const user = getCurrentUser()

  if (!user) {
    throw new Error('User not authenticated')
  }
  return user.uid
}

const createCollectionRefFunc = (
  authenticated: boolean,
  name: string
): (() => CollectionReference) =>
  authenticated
    ? (): CollectionReference =>
        collection(getDbInstance(), 'users', getUserUID(), name)
    : (): CollectionReference => collection(getDbInstance(), name)

const createDocumentRefFunc = (
  authenticated: boolean,
  name: string
): ((id: DocID) => DocumentReference) =>
  authenticated
    ? (id: DocID): DocumentReference =>
        doc(getDbInstance(), 'users', getUserUID(), name, id)
    : (id: DocID): DocumentReference => doc(getDbInstance(), name, id)

type ShowQueryOptions<TDoc, TData, TStrict extends boolean = true> = {
  enabled?: boolean
  select?: (data: TDoc) => TData
  throwOnNotFound?: TStrict
}

export type ShowQueryOptionsResult<T extends z.ZodObject> = {
  queryKey: QueryKey
  queryFn: QueryFunction<TDoc<T>>
  retry: false
}

export type FirestoreApi<T extends z.ZodObject> = {
  useShowQuery: <TData = TDoc<T>, TStrict extends boolean = true>(
    id: DocID,
    options?: ShowQueryOptions<TDoc<T>, TData, TStrict>
  ) => UseQueryResult<TStrictResult<TData, TStrict>, Error>
  getShowQueryOptions: (id: DocID) => ShowQueryOptionsResult<T>
  useListQuery: ({
    filter,
    enabled,
  }?: {
    filter?: (items: TDoc<T>) => boolean
    enabled?: boolean
  }) => UseQueryResult<TDoc<T>[], Error>
  useCreateMutation: () => UseMutationResult<DocID, Error, unknown>
  useUpdateMutation: () => UseMutationResult<void, Error, [DocID, unknown]>
  useUpsertMutation: () => UseMutationResult<void, Error, [DocID, unknown]>
  useDeleteMutation: () => UseMutationResult<void, Error, DocID>
  /**
   * Deletes one document, outside React Query.
   *
   * For a caller composing a delete with other writes in one mutation — a
   * journey taking its quests with it — where a hook cannot be called. It
   * invalidates nothing; the composing mutation owns that.
   */
  remove: (id: DocID) => Promise<void>
}

export const createFirestoreApi = <T extends z.ZodObject>({
  authenticated,
  name,
  schema,
}: {
  authenticated: boolean
  name: string
  schema: T
}): FirestoreApi<T> => {
  const docSchema = createDocSchema(schema)
  const strictSchema = schema.strict()

  const getCollectionRef = createCollectionRefFunc(authenticated, name)
  const getDocumentRef = createDocumentRefFunc(authenticated, name)

  // An authenticated collection lives under `users/{uid}`, so its cache entries
  // have to be scoped the same way or two accounts share one entry (ZL-985).
  // The uid goes after the name, which keeps the collection name as the prefix
  // so callers that invalidate by bare name still match. It is read per call:
  // there is no signed-in user yet when this factory runs at import time.
  const scope = (): QueryKey =>
    authenticated ? [name, getCurrentUser()?.uid ?? null] : [name]

  const keys = {
    all: scope,
    lists: (): QueryKey => [...scope(), 'list'],
    details: (): QueryKey => [...scope(), 'detail'],
    detail: (id: string): QueryKey => [...keys.details(), { id }],
  }

  const fromFirestore = (snap: QueryDocumentSnapshot): TDoc<T> => {
    const id = snap.id
    // A document written while Firebase is unreachable is applied to the local
    // cache optimistically, with its serverTimestamp() fields still pending.
    // Without 'estimate' those fields read back as null and fail schema
    // validation; 'estimate' substitutes a local-clock value that self-corrects
    // once the write syncs.
    const { createdAt, updatedAt, ...entity } = snap.data({
      serverTimestamps: 'estimate',
    })
    return docSchema.parse({
      id,
      entity,
      createdAt,
      updatedAt,
    })
  }

  // Hermes has issues with inline arrow async functions resolving closure
  // variables — inlining these back into hooks will break on mobile.
  // See: https://github.com/facebook/hermes/issues/398
  const listDocs = async (): Promise<TDoc<T>[]> => {
    const collectionRef = getCollectionRef()
    const qs = await getDocs(collectionRef)
    return qs.docs.map(fromFirestore)
  }

  const showDoc = async (id: DocID): Promise<TDoc<T> | null> => {
    const documentRef = getDocumentRef(id)
    const snap = await getDoc(documentRef)
    if (!snap.exists()) return null
    return fromFirestore(snap)
  }

  const createDoc = async (params: unknown): Promise<DocID> => {
    const data = strictSchema.parse(params)
    const collectionRef = getCollectionRef()
    const ref = await withWriteTimeout(
      addDoc(collectionRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    )
    return ref.id
  }

  const updateDocFn = async ([id, params]: [DocID, unknown]): Promise<void> => {
    const data = schema.partial().parse(params)
    const documentRef = getDocumentRef(id)
    await withWriteTimeout(
      updateDoc(documentRef, {
        ...data,
        updatedAt: serverTimestamp(),
      })
    )
  }

  const upsertDocFn = async ([id, params]: [DocID, unknown]): Promise<void> => {
    const data = strictSchema.parse(params)
    const documentRef = getDocumentRef(id)
    await withWriteTimeout(
      setDoc(documentRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    )
  }

  const deleteDocFn = async (id: DocID): Promise<void> => {
    const documentRef = getDocumentRef(id)
    await withWriteTimeout(deleteDoc(documentRef))
  }

  // Strict-mode show-query options (throws DocumentNotFoundError when the
  // document is missing). Reused by both the React hook and route loaders
  // so they share the same cache entry.
  const getShowQueryOptions = (id: DocID): ShowQueryOptionsResult<T> => ({
    queryKey: keys.detail(id),
    queryFn: async (): Promise<TDoc<T>> => {
      const result = await showDoc(id)
      if (result === null) {
        throw new DocumentNotFoundError('Document not found')
      }
      return result
    },
    retry: false,
  })

  const useShowQuery = <TData = TDoc<T>, TStrict extends boolean = true>(
    id: DocID,
    options?: ShowQueryOptions<TDoc<T>, TData, TStrict>
  ): UseQueryResult<TStrictResult<TData, TStrict>, Error> => {
    const { enabled = true, select, throwOnNotFound = true } = options ?? {}

    const nullSafeSelect = select
      ? (data: TDoc<T> | null): TData | null =>
          data === null ? null : select(data)
      : undefined

    // Safe cast: when throwOnNotFound is true queryFn throws (never null),
    // when false the wider TData | null is the correct type.
    return useQuery<TDoc<T> | null, Error, TData | null>({
      enabled,
      queryKey: keys.detail(id),
      queryFn: async () => {
        const result = await showDoc(id)
        if (result === null && throwOnNotFound) {
          throw new DocumentNotFoundError('Document not found')
        }
        return result
      },
      select: nullSafeSelect,
      retry: false,
    }) as UseQueryResult<TStrictResult<TData, TStrict>, Error>
  }

  const useListQuery = ({
    filter,
    enabled = true,
  }: {
    filter?: (items: TDoc<T>) => boolean
    enabled?: boolean
  } = {}): UseQueryResult<TDoc<T>[], Error> => {
    const selectFn = useCallback(
      (items: TDoc<T>[]) => (filter ? items.filter(filter) : items),
      [filter]
    )

    return useQuery({
      enabled,
      queryKey: keys.lists(),
      queryFn: listDocs,
      select: selectFn,
      retry: false,
    })
  }

  const useCreateMutation = (): UseMutationResult<DocID, Error, unknown> => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: createDoc,
      onSuccess: () => qc.invalidateQueries({ queryKey: keys.lists() }),
    })
  }

  const useUpdateMutation = (): UseMutationResult<
    void,
    Error,
    [DocID, unknown]
  > => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: updateDocFn,
      onSuccess: (_, [id]) => {
        qc.invalidateQueries({ queryKey: keys.detail(id) })
        qc.invalidateQueries({ queryKey: keys.lists() })
      },
    })
  }

  const useUpsertMutation = (): UseMutationResult<
    void,
    Error,
    [DocID, unknown]
  > => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: upsertDocFn,
      onSuccess: (_, [id]) => {
        qc.invalidateQueries({ queryKey: keys.lists() })
        qc.invalidateQueries({ queryKey: keys.detail(id) })
      },
    })
  }

  const useDeleteMutation = (): UseMutationResult<void, Error, DocID> => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: deleteDocFn,
      onSuccess: (_, id) => {
        qc.invalidateQueries({ queryKey: keys.detail(id) })
        qc.invalidateQueries({ queryKey: keys.lists() })
      },
    })
  }

  return {
    useShowQuery,
    getShowQueryOptions,
    useListQuery,
    useCreateMutation,
    useUpdateMutation,
    useUpsertMutation,
    useDeleteMutation,
    remove: deleteDocFn,
  }
}
